/* eslint-disable consistent-return,no-unused-expressions */
import debounce from 'lodash/debounce';
import get from 'lodash/get';

import { ReduxEmitter } from '../helpers/reduxEmitter';
import { otEventManager } from '../helpers/eventManager';
import { OT_EVENTS, PUB_KEY } from '../helpers/constants';
import { emitForceUpdate } from '../helpers/notifications';
import { getMediaDevices, parseUserPayload } from '../helpers/utils';

import {
  sentryCaptureError,
  trackAppAction,
  trackError,
} from '../../../utils/sentry/sentryMethods';

export class OTPublisher extends ReduxEmitter {
  id = PUB_KEY;

  constructor({ user, dispatch, otSession }) {
    super(dispatch);
    this.user = user;
    this.otSession = otSession;
    this.publisher = null;
    this.initialMediaDevices = null;
    this.ready = false;
  }

  get publisherOptions() {
    return {
      insertDefaultUI: false,
      showControls: true,
      resolution: '640x480',
      ...(this.initialMediaDevices && {
        videoSource: get(
          this.initialMediaDevices,
          'videoSourceList[0].deviceId',
        ),
        audioSource: get(
          this.initialMediaDevices,
          'audioSourceList[0].deviceId',
        ),
      }),
    };
  }

  get session() {
    if (!this.otSession || !this.otSession.session) {
      console.warn('[ot-publisher] no session');
      return null;
    }

    return this.otSession.session;
  }

  async updateDeviceList() {
    try {
      this.initialMediaDevices = await getMediaDevices();
    } catch (error) {
      console.error('[ot-publisher] |updateDeviceList|', error);
      trackError(error);
      sentryCaptureError('[ERROR] MEDIA DEVICE LIST');
      throw error;
    }
  }

  initiatePublisher() {
    return new Promise((resolve, reject) => {
      console.time('init-publisher');
      const publisher = OT.initPublisher('', this.publisherOptions, error => {
        if (error) {
          console.error('[ot-publisher] |initiatePublisher|', error);
          console.timeEnd('init-publisher');
          return reject(error);
        }
        console.timeEnd('init-publisher');
        return resolve(publisher);
      });
    });
  }

  handleDeviceChange = debounce(async () => {
    console.log('[ot-publisher] media device change');
    await this.updateDeviceList();
    const { videoSourceList, audioSourceList } = this.initialMediaDevices;
    const [defaultVideoDevice] = videoSourceList;
    const [defaultAudioDevice] = audioSourceList;
    const activeVideoSource = this.publisher.getVideoSource();
    const activeAudioSource = this.publisher.getAudioSource();
    let activeAudioDeviceId;

    if (activeAudioSource && activeAudioSource.getCapabilities) {
      activeAudioDeviceId = get(
        activeAudioSource.getCapabilities(),
        'deviceId',
      );
    }

    const isVideoEnded = get(activeVideoSource, 'track.readyState') === 'ended';
    const isAudioEnded = get(activeAudioSource, 'readyState') === 'ended';

    const videoDeviceExist = videoSourceList.some(
      ({ deviceId }) => deviceId === activeVideoSource.deviceId,
    );
    const audioDeviceExist = audioSourceList.some(
      ({ deviceId }) => deviceId === activeAudioDeviceId,
    );

    /** VIDEO FALLBACK */
    if (defaultVideoDevice && (!videoDeviceExist || isVideoEnded)) {
      const message = !videoDeviceExist ? 'does not exist' : 'has ENDED state';
      console.warn(`actual video device ${message}`);
      await this.changeVideoSource(defaultVideoDevice.deviceId);
      emitForceUpdate();
    }

    /** AUDIO FALLBACK */
    if (defaultAudioDevice && (!audioDeviceExist || isAudioEnded)) {
      const message = !audioDeviceExist ? 'does not exist' : 'has ENDED state';
      console.warn(`actual video device  ${message}`);
      await this.changeAudioSource(defaultAudioDevice.deviceId);
      emitForceUpdate();
    }

    emitForceUpdate();
  }, 300);

  setupListeners() {
    if (!this.publisher) throw new Error('No Publisher defined');

    console.log('[ot-publisher] setup listeners');
    this.publisher.on(OT_EVENTS.STREAM_CREATED, ({ stream }) => {
      if (this.otSession) {
        this.otSession.setPublisherStreamId(stream.streamId);
      }
      this.dispatchUpdateConnection({ stream, id: this.id });
    });

    this.publisher.on(OT_EVENTS.STREAM_DESTROYED, event => {
      trackAppAction(`[ot-publisher] stream destroyed ${event.reason}`);
      if (event.reason === 'mediaStopped') {
        console.warn('[ot-publisher] |STREAM_DESTROYED| mediaStopped');
        this.ready = false;
        otEventManager.emitReconnect();
        return;
      }
      if (event.reason === 'networkDisconnected') {
        console.warn('[ot-publisher] |STREAM_DESTROYED| networkDisconnected');
        return;
      }
      if (event.reason === 'clientDisconnected') {
        console.warn('[ot-publisher] |STREAM_DESTROYED| clientDisconnected');
        this.ready = false;
        otEventManager.emitReconnect();
        return;
      }

      console.warn(
        '[ot-publisher] TODO: HANDLE DESTROY PUBLISHER STREAM ',
        event.reason,
        event,
      );
    });

    if (navigator.mediaDevices) {
      navigator.mediaDevices.addEventListener(
        'devicechange',
        this.handleDeviceChange,
      );
    }
  }

  clearListeners() {
    if (this.publisher) {
      console.log('[ot-publisher] clear listeners');
      this.publisher.off();
    }

    if (navigator.mediaDevices) {
      navigator.mediaDevices.removeEventListener(
        'devicechange',
        this.handleDeviceChange,
      );
    }
  }

  async changeVideoSource(deviceId) {
    if (!this.publisher) throw new Error('no Publisher');
    if (!this.ready) {
      console.warn(
        '[ot-publisher] |changeVideoSource| SKIP, cause publisher is not ready',
      );
      return;
    }

    trackAppAction(`[ot-publisher] change video source ${deviceId}`);

    try {
      console.time('change-video-source');
      await this.publisher.setVideoSource(deviceId);
      console.info('[ot-publisher] VIDEO CHANGED');
    } catch (e) {
      console.error('[ot-publisher] ERROR DURING VIDEO CHANGE', e);
      trackError(e);
      sentryCaptureError('[ERROR] CHANGE VIDEO DEVICE');
    } finally {
      console.timeEnd('change-video-source');
    }
  }

  async changeAudioSource(deviceId) {
    if (!this.publisher) throw new Error('no Publisher');
    if (!this.ready) {
      console.warn(
        '[ot-publisher] |changeAudioSource| SKIP, cause publisher is not ready',
      );
      return;
    }

    trackAppAction(`[ot-publisher] change audio source ${deviceId}`);

    try {
      console.time('change-audio-source');
      await this.publisher.setAudioSource(deviceId);
      console.info('[ot-publisher] AUDIO CHANGED');
    } catch (e) {
      console.error('[ot-publisher] ERROR DURING AUDIO CHANGE', e);
      trackError(e);
      sentryCaptureError('[ERROR] CHANGE AUDIO DEVICE');
    } finally {
      console.timeEnd('change-audio-source');
    }
  }

  async setupPublisher() {
    try {
      await this.updateDeviceList();
      this.publisher = await this.initiatePublisher();
      this.dispatchAddConnection({
        entity: this.publisher,
        user: this.user,
        ready: false,
        id: this.id,
      });

      setImmediate(() => {
        const video = this.publisher.videoElement();
        this.dispatchUpdateConnection({ ready: true, video, id: this.id });
      });

      this.setupListeners();
    } catch (error) {
      console.error('[ot-publisher] |setupPublisher|', error);
      trackError(error);
      sentryCaptureError('[ERROR] SETUP PUBLISHER');
      throw error;
    }
  }

  updateUser() {
    if (this.user.id) return;

    try {
      const user = parseUserPayload(this.session.connection.data);
      this.dispatchUpdateConnection({ id: this.id, user });
    } catch (e) {
      console.log('error during update user', e);
    }
  }

  publishIntoSession() {
    this.updateUser();
    return new Promise((resolve, reject) => {
      if (!this.session) return reject(new Error('No Session'));
      if (!this.publisher) return reject(new Error('No Publisher'));

      console.time('publish-into-session');
      this.session.publish(this.publisher, error => {
        if (error) {
          console.error('[ot-publisher] |session.publish|', error);
          this.destroy();
          console.timeEnd('publish-into-session');
          return reject(error);
        }

        console.timeEnd('publish-into-session');
        this.ready = true;
        resolve(true);
      });
    });
  }

  destroy() {
    this.ready = false;
    this.clearListeners();
    if (this.publisher) {
      this.publisher.destroy();
    }
    if (this.session && this.publisher) {
      console.info('[ot-publisher] unpublish publisher from session');
      this.session.unpublish(this.publisher);
    }
    console.log('[ot-publisher] destroyed');
  }
}



// WEBPACK FOOTER //
// ./src/redux/opentok/core/publisher.js